import { Select as AntSelect, Tooltip as AntTooltip } from "antd";
import { Option, ReadonlyArray, Runtime } from "fp";

import React, { useMemo } from "react";

import { Company } from "~/Company";
import * as Driver from "~/Driver";
import { useWorkReportingLocationFilters } from "~/Driver/Filter";
import * as GenericFilter from "~/Filter";
import { ID } from "~/ID";
import { Location as GenericLocation } from "~/Location";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Request } from "~/Request";

export type Location = GenericLocation & {
  readonly location: ID & { readonly nickname?: string };
};

export function Location({
  location,
  alwaysDisplayAddress = false,
  labelPrefix = undefined,
}: Location.Props) {
  return location.nickname && location.address ? (
    <AntTooltip
      title={
        <GenericLocation.Address
          address={location.address}
          labelPrefix={labelPrefix}
        />
      }
      mouseEnterDelay={0.5}
    >
      {alwaysDisplayAddress
        ? Location.toString({ location })
        : location.nickname}
    </AntTooltip>
  ) : location.nickname ? (
    <>{location.nickname}</>
  ) : location.gps ? (
    <GenericLocation.GPS gps={location.gps} />
  ) : (
    <>Location {location.id}</>
  );
}

export namespace Location {
  export namespace Filter {
    type Props = {
      readonly onFilter: (locationIds: Driver.Filter.Filter) => void;
    };

    export const Component = ({ onFilter }: Props): JSX.Element => {
      const processedLocations = useLocationsAsFilterOptions();

      const onClose = (allLocations: readonly GenericFilter.FilterOption[]) => {
        const selectedLocations = allLocations
          .filter(({ checked }) => checked === "on")
          .map(({ data }) => Number(data.id));

        onFilter && onFilter(selectedLocations);
      };

      return (
        <GenericFilter.Filter
          isLoading={false}
          onClose={onClose}
          label="Work Reporting Location"
          initialItems={processedLocations}
        ></GenericFilter.Filter>
      );
    };
  }

  // eslint-disable-next-line functional/prefer-readonly-type
  export const useLocationsAsFilterOptions = (): GenericFilter.FilterOption[] => {
    const [preferencesLoading, preferences] = useWorkReportingLocationFilters();

    const [locationsLoading, locations] = Remote.when(
      Company.Locations.WorkReportingLocation.use(),
      {
        loading: () => [true, []],
        error: () => [false, []],
        success: ({ locations }) => {
          return [
            false,
            pipe(locations, ReadonlyArray.sort(Company.Location.order())),
          ];
        },
      }
    );

    const formatLocation = (
      location: Company.Location
    ): GenericFilter.FilterOption => {
      return {
        label: Location.toString(location),
        checked:
          preferences && preferences.includes(Number(location.location.id))
            ? "on"
            : undefined,
        data: { id: location.location.id },
      };
    };

    return useMemo(() => {
      if (preferencesLoading || locationsLoading) {
        return [];
      }

      return locations.map(formatLocation);
    }, [preferencesLoading, locationsLoading, preferences, locations.length]);
  };

  export type Props = Location & {
    readonly alwaysDisplayAddress?: boolean;
    readonly labelPrefix?: string;
  };

  export const toString = ({
    location: { id, nickname, address },
  }: Location): string => {
    const location = [
      nickname,
      !!address && GenericLocation.Address.toString({ address }),
    ]
      .filter((part) => !!part)
      .join(" – ");

    return location === "" ? `Location ${id}` : location;
  };

  export const order = () =>
    Order.fromCompare<Location>((a, b) =>
      Order.string().compare(
        Location.toString(a).toLowerCase(),
        Location.toString(b).toLowerCase()
      )
    );

  export const { equals, compare } = order();

  export namespace Response {
    export const runtime = () =>
      Runtime.intersection([
        GenericLocation.Address.Response.runtime(),
        Runtime.strict({
          location_id: Runtime.number(),
          nickname: Runtime.nullable(Runtime.string()),
          latitude: Runtime.nullable(Runtime.number()),
          longitude: Runtime.nullable(Runtime.number()),
        }),
      ]);

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Location | undefined => {
      const { id } = ID.from(response.location_id);
      if (id === "0") return;

      const gps = typeof response.latitude === "number" &&
        typeof response.longitude === "number" && {
          gps: {
            latitude: response.latitude,
            longitude: response.longitude,
          },
        };

      return {
        location: {
          id,
          ...GenericLocation.Address.Response.decode(response),
          ...gps,
          nickname: response.nickname ?? undefined,
        },
      };
    };
  }
}

export type Locations = { readonly locations: readonly Location[] };
export namespace Locations {
  export const empty = (): Locations => ({ locations: [] });

  /** [API Documentation](http://equipmentshare.pages.internal.equipmentshare.com/equipmentshare-api/master/index.html?highlight=locations#get--companies-(int-company_id)-locations) */
  export const request = ({
    company,
  }: {
    readonly company: ID;
  }): Request.Fn<Locations> => {
    return flow(
      Request.fromConfig({ endpoint: `/companies/${company.id}/locations` }),
      Request.Expect.status(200),
      Request.Expect.json(Response.runtime()),
      Request.onSuccess(Response.decode)
    );
  };

  export const use = (): Remote<Locations> => {
    const { company } = Company.use();
    return Remote.Query.use(
      ["Company Locations", company.id],
      request({ company })
    );
  };

  export namespace WorkReportingLocation {
    export const request = ({
      company,
    }: {
      readonly company: ID;
    }): Request.Fn<Locations> => {
      return flow(
        Request.fromConfig({
          endpoint: `/elogs/companies/${company.id}/work_reporting_locations`,
        }),
        Request.Expect.status(200),
        Request.Expect.json(Response.runtime()),
        Request.onSuccess(Response.decode)
      );
    };

    export const use = (): Remote<Locations> => {
      const { company } = Company.use();
      return Remote.Query.use(
        ["Company Work Reporting Locations", company.id],
        request({ company })
      );
    };
  }

  namespace Response {
    export const runtime = () => Runtime.array(Location.Response.runtime());
    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Locations =>
      pipe(
        response,
        ReadonlyArray.filterMap(
          flow(Location.Response.decode, Option.fromNullable)
        ),
        (locations) => ({ locations })
      );
  }

  export function Select({ selected, select, clear, ...props }: Select.Props) {
    const locations = use();
    const options = Select.Options.fromLocations(
      Remote.toUndefined(locations) ?? empty()
    );

    const value = pipe(
      selected?.locations ?? [],
      ReadonlyArray.map(({ location }) => location.id)
    );

    const onChange = (ids: readonly string[]) =>
      pipe(
        options,
        ReadonlyArray.filter(({ location }) => ids.includes(location.id)),
        (locations) => select({ locations })
      );

    const filterOption = (value: string, option: Select.Option) =>
      Location.toString(option)
        .toLocaleLowerCase()
        .includes(value.toLocaleLowerCase());

    type AntSelectProps = React.ComponentProps<typeof AntSelect>;
    return (
      <AntSelect
        {...props}
        showArrow
        mode="multiple"
        loading={Remote.isLoading(locations)}
        filterOption={filterOption as AntSelectProps["filterOption"]}
        value={value as AntSelectProps["value"]}
        options={(options as never) as AntSelectProps["options"]}
        onChange={onChange as AntSelectProps["onChange"]}
        onClear={clear}
      />
    );
  }

  export namespace Select {
    export type Props = Controls &
      Pick<
        React.ComponentProps<typeof AntSelect>,
        "bordered" | "placeholder" | "style"
      >;

    export type Controls = {
      readonly selected?: Partial<Locations>;
      readonly select: (locations: Partial<Locations>) => void;
      readonly clear: () => void;
    };

    export namespace Controls {
      export const use = (): Controls => {
        const [selected, setSelected] = React.useState<Locations>(empty());
        const select = ({ locations }: Partial<Locations>) =>
          pipe(
            [...selected.locations, ...(locations ?? [])],
            ReadonlyArray.uniq({
              equals: (a, b) => a.location.id === b.location.id,
            }),
            (locations) => setSelected({ locations })
          );

        return { selected, select, clear: () => setSelected(empty()) };
      };
    }

    export type Option = Location & {
      readonly value: string;
      readonly label: React.ReactNode;
    };

    export type Options = readonly Option[];
    export namespace Options {
      export const fromLocations = ({ locations }: Locations): Options =>
        pipe(
          locations,
          ReadonlyArray.sort<Location>(
            Order.fromCompare((a, b) =>
              Order.string().compare(Location.toString(a), Location.toString(b))
            )
          ),
          ReadonlyArray.map(({ location }) => ({
            location,
            value: location.id,
            label: <Location alwaysDisplayAddress location={location} />,
          }))
        );
    }
  }

  export function SelectOne({ selected, select }: SelectOne.Props) {
    return pipe(
      use(),
      Remote.when({
        loading: () => <AntSelect loading />,
        error: () => <AntSelect loading />,
        success: flow(
          ({ locations }) => locations,
          ReadonlyArray.map(({ location }) => ({
            value: location.id,
            label: Location.toString({ location }),
          })),
          ReadonlyArray.sort<{ readonly label: string }>(
            Order.fromCompare((a, b) =>
              Order.string().compare(a.label, b.label)
            )
          ),
          (options) => (
            <AntSelect
              showSearch
              dropdownMatchSelectWidth={false}
              value={selected?.location.id}
              onChange={(id) => select({ location: { id } })}
              filterOption={(value, option) =>
                !!option?.label?.toString().toLocaleLowerCase().includes(value)
              }
              options={
                options as React.ComponentProps<typeof AntSelect>["options"]
              }
            />
          )
        ),
      })
    );
  }

  export namespace SelectOne {
    export type Props = {
      readonly selected?: Location;
      readonly select: (location: Location) => void;
    };

    export const use = (initial: Partial<Location>): Props => {
      const location = initial?.location;
      const [selected, select] = React.useState(location && { location });
      return { selected, select };
    };

    export const initialize = ({ select }: Props) => (
      initial: Partial<Location>
    ): void => {
      const location = initial?.location;
      if (location) {
        select({ location });
      }
    };
  }
}
